import { type ReactNode, useEffect, useState } from "react";
import { organization, signOut, useListOrganizations, useSession } from "./auth-client";
import { invitationIdFromSearch } from "./invite-query";
import { Login } from "./screens/Login";
import { isPlatformAdminRole } from "../api/platform-roles";

const SIGNUP_ENABLED = false;

const inviteInFlight = new Map<string, Promise<boolean>>();

function acceptInvitationOnce(invitationId: string): Promise<boolean> {
  const existing = inviteInFlight.get(invitationId);
  if (existing) return existing;
  const attempt = (async () => {
    try {
      const res = await organization.acceptInvitation({ invitationId });
      if (!res.error) return true;
      return res.error.code === "USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION";
    } catch {
      return false;
    }
  })();
  inviteInFlight.set(invitationId, attempt);
  return attempt;
}

function stripInvitationQuery(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("invitation");
  url.searchParams.delete("invitationId");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function GateLoading() {
  return (
    <div className="login-wrap">
      <div style={{ color: "var(--text-2)", fontSize: 13 }}>Caricamento…</div>
    </div>
  );
}

function GateCard({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1 className="login-title">{title}</h1>
        <div className="login-sub">{sub}</div>
        <div className="row gap-8">
          <button
            className="btn primary"
            onClick={() => {
              void signOut();
            }}
          >
            Esci
          </button>
        </div>
      </div>
    </div>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { data: session, isPending, refetch } = useSession();
  const { data: orgs, isPending: orgsPending, refetch: refetchOrgs } = useListOrganizations();
  const [sessionChecked, setSessionChecked] = useState(!isPending);
  const [invitationId] = useState(() => invitationIdFromSearch(window.location.search));
  const [inviteState, setInviteState] = useState<"pending" | "ok" | "error">(() =>
    invitationId ? "pending" : "ok",
  );

  useEffect(() => {
    if (!isPending) setSessionChecked(true);
  }, [isPending]);

  useEffect(() => {
    if (!session) return;
    if (new URLSearchParams(window.location.search).get("download") !== "extension") return;
    window.location.replace("/api/extension/download");
  }, [session]);

  useEffect(() => {
    if (session || !invitationId) return;
    inviteInFlight.delete(invitationId);
    setInviteState("pending");
  }, [session, invitationId]);

  useEffect(() => {
    if (!session || !invitationId || inviteState !== "pending") return;
    let cancelled = false;
    void (async () => {
      const ok = await acceptInvitationOnce(invitationId);
      if (cancelled) return;
      if (!ok) {
        setInviteState("error");
        return;
      }
      try {
        await Promise.all([refetch(), refetchOrgs()]);
      } catch {
        // l'invito è già accettato; non bloccare se il refetch fallisce
      }
      if (cancelled) return;
      stripInvitationQuery();
      setInviteState("ok");
    })();
    return () => {
      cancelled = true;
    };
  }, [session, invitationId, inviteState, refetch, refetchOrgs]);

  if (isPending && !sessionChecked) return <GateLoading />;

  if (!session) {
    return <Login signupEnabled={SIGNUP_ENABLED} onSuccess={() => {
      void refetch();
      void refetchOrgs();
    }} />;
  }

  if (!isPlatformAdminRole(session.user.role) && session.user.role !== "user") {
    return (
      <GateCard
        title="Accesso limitato"
        sub="Questo account non è abilitato ad accedere a Legacy."
      />
    );
  }

  if (invitationId && inviteState === "pending") return <GateLoading />;

  const isPlatformAdmin = isPlatformAdminRole(session.user.role);
  if (!isPlatformAdmin) {
    if (orgsPending) return <GateLoading />;
    if (!orgs || orgs.length === 0) {
      if (inviteState === "error") {
        return (
          <GateCard
            title="Invito non valido o scaduto"
            sub="Questo link non è più utilizzabile. Chiedi un nuovo invito a un amministratore."
          />
        );
      }
      return (
        <GateCard
          title="Nessuna azienda"
          sub="Il tuo account è attivo, ma non sei associato a nessuna azienda. Chiedi a un amministratore di invitarti, poi esci e accedi di nuovo."
        />
      );
    }
  }

  return <>{children}</>;
}
